import React, { useCallback, useMemo } from 'react';
import { FieldLabel } from '@puckeditor/core';
import styled from 'styled-components';

import { isSurfaceColorRole, resolveSurfaceColor } from '../tokens';
import type { SurfaceColorRole } from '../tokens';
import { useCmsEnterpriseId } from '../cms-enterprise-context';
import { sanitizeHexInput } from './color-role-field';
import { useAssetPicker } from './asset-picker-control';
import { LibraryUrlTabs } from './library-url-tabs';

export type BackgroundValue = {
  color: string;
  image: string;
  size: string;
  position: string;
  repeat: string;
};

export const DEFAULT_BACKGROUND: BackgroundValue = {
  // 'surface' resolves to `--dcm-color-surface` (fallback #ffffff) — no visual change,
  // but the default now follows the merchant theme rather than a baked-in hex.
  color: 'surface',
  image: '',
  size: 'cover',
  position: 'center',
  repeat: 'no-repeat',
};

const SIZE_OPTIONS = [
  { label: 'Cover', value: 'cover' },
  { label: 'Contain', value: 'contain' },
  { label: 'Auto', value: 'auto' },
] as const;

const POSITION_OPTIONS = [
  { label: 'Center', value: 'center' },
  { label: 'Top', value: 'top' },
  { label: 'Bottom', value: 'bottom' },
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' },
  { label: 'Top Left', value: 'top left' },
  { label: 'Top Right', value: 'top right' },
  { label: 'Bottom Left', value: 'bottom left' },
  { label: 'Bottom Right', value: 'bottom right' },
] as const;

const REPEAT_OPTIONS = [
  { label: 'No Repeat', value: 'no-repeat' },
  { label: 'Repeat', value: 'repeat' },
  { label: 'Repeat X', value: 'repeat-x' },
  { label: 'Repeat Y', value: 'repeat-y' },
] as const;

/** Starting color when the author switches from a theme role to a custom hex. */
const CUSTOM_DEFAULT = '#f5f6f7';

/** Options offered by the background color dropdown, in display order. */
const SURFACE_ROLE_OPTIONS: readonly { label: string; value: SurfaceColorRole }[] = [
  { label: 'Transparent', value: 'none' },
  { label: 'Surface', value: 'surface' },
  { label: 'Muted', value: 'muted' },
];

const CUSTOM_OPTION_VALUE = 'custom';

const COMPLETE_HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Converts a `BackgroundValue` into React inline style properties.
 *
 * Accepts `undefined` and emits no declarations for it. This is *not* backwards-compatibility
 * machinery -- it is the same unvalidated-stored-data rule the resolvers in this directory follow
 * (see `border.ts#borderStyle` and `resolveBorderColor`): persisted page data is JSON with no
 * runtime type enforcement, so a value violating the declared type can arrive here, and it must
 * never crash the surrounding page.
 *
 * Load-bearing rather than theoretical: `Section`/`FlexLayout` predate this field, Puck merges
 * `defaultProps` into saved props in the *editor* (`dist/index.js:10767`) but **not** on the
 * production `<Render>` path (`DropZoneRenderItem` passes `item.props` straight through), and
 * neither renderer has an error boundary -- so an already-published page with no `background` key
 * would white-screen entirely while still looking correct in the editor.
 *
 * Note this returns `{}` rather than the old `{ backgroundColor: <surface token> }` default: on
 * `FlexLayout` (an inner container) that default painted an opaque fill over whatever sat behind
 * it, which is why its call site used to guard the spread. Returning nothing keeps an absent key
 * visually identical to "no background", for both components, without a call-site conditional.
 *
 * `background` itself stays **required** on `SectionProps`/`FlexLayoutProps` -- the type governs
 * what authoring code may pass, this guard governs what stored JSON can actually contain.
 */
export function backgroundStyle(bg: BackgroundValue | undefined): React.CSSProperties {
  if (!bg) return {};

  const style: React.CSSProperties = { backgroundColor: resolveSurfaceColor(bg.color) };
  if (bg.image) {
    const escapedUrl = bg.image.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    style.backgroundImage = `url("${escapedUrl}")`;
    style.backgroundSize = bg.size || DEFAULT_BACKGROUND.size;
    style.backgroundPosition = bg.position || DEFAULT_BACKGROUND.position;
    style.backgroundRepeat = bg.repeat || DEFAULT_BACKGROUND.repeat;
  }
  return style;
}

/** AI schema for the BackgroundValue object. */
export const BACKGROUND_AI_SCHEMA = {
  type: 'object',
  required: ['color'],
  properties: {
    color: {
      type: 'string',
      description:
        "Either a surface theme role ('none', 'surface', 'muted') or a hex value such as #ff0000. Prefer a role so the background follows merchant branding.",
    },
    image: { type: 'string' },
    size: { type: 'string', enum: ['cover', 'contain', 'auto'] },
    position: {
      type: 'string',
      enum: ['center', 'top', 'bottom', 'left', 'right', 'top left', 'top right', 'bottom left', 'bottom right'],
    },
    repeat: { type: 'string', enum: ['no-repeat', 'repeat', 'repeat-x', 'repeat-y'] },
  },
};

type BackgroundFieldProps = {
  field: { label?: string };
  name: string;
  value: BackgroundValue | undefined;
  onChange: (value: BackgroundValue) => void;
  readOnly?: boolean;
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid #d0d4d8',
  borderRadius: 4,
  fontSize: 14,
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  background: '#fff',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 8,
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--dcm-cms-bg-field-label-color, #646d72)',
  fontWeight: 500,
  minWidth: 52,
};

const ModeGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 8px;
  border: 1px solid
    var(--dcm-cms-bg-field-mode-border-color, var(--puck-field-color-border, var(--puck-color-border, #d0d4d8)));
  border-radius: var(--dcm-cms-bg-field-mode-border-radius, var(--puck-field-radius, var(--puck-radius-m, 6px)));
  overflow: hidden;
`;

const ModeOption = styled.label<{ $selected: boolean }>`
  position: relative;
  padding: 8px 10px;
  background: ${({ $selected }) =>
    $selected
      ? 'var(--dcm-cms-bg-field-mode-selected-bg, var(--puck-color-interactive-soft, #e7f5fc))'
      : 'var(--dcm-cms-bg-field-mode-bg, var(--puck-field-color-bg, var(--puck-color-surface, #fff)))'};
  color: ${({ $selected }) =>
    $selected
      ? 'var(--dcm-cms-bg-field-mode-selected-text-color, var(--puck-color-interactive, #0b99e6))'
      : 'var(--dcm-cms-bg-field-mode-text-color, var(--puck-field-color-text, var(--puck-color-text, #121516)))'};
  cursor: pointer;
  font-size: var(--puck-field-font-size, var(--puck-font-size-xxs, 13px));
  font-weight: 600;
  text-align: center;

  & + & {
    border-left: 1px solid
      var(--dcm-cms-bg-field-mode-border-color, var(--puck-field-color-border, var(--puck-color-border, #d0d4d8)));
  }

  &:focus-within {
    outline: var(--puck-border-width-focus, 2px) solid
      var(--dcm-cms-bg-field-mode-focus-ring-color, var(--puck-color-focus-ring, #0b99e6));
    outline-offset: -2px;
    z-index: 1;
  }

  &:has(input:disabled) {
    cursor: default;
    color: var(
      --dcm-cms-bg-field-mode-disabled-text-color,
      var(--puck-field-color-text-disabled, var(--puck-color-text-secondary, #646d72))
    );
  }
`;

const ModeInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;

function isInheritedBackground(background: BackgroundValue): boolean {
  return background.color === 'none' && background.image === '';
}

/** Composite Puck field widget for background styling (color + optional image settings). */
export function BackgroundField({ field, name, value, onChange, readOnly }: BackgroundFieldProps): JSX.Element {
  const bg = useMemo(() => ({ ...DEFAULT_BACKGROUND, ...value }), [value]);
  const enterpriseId = useCmsEnterpriseId();

  const isInherited = isInheritedBackground(bg);
  const isCustom = !isSurfaceColorRole(bg.color);

  const colorSelectValue = isCustom ? CUSTOM_OPTION_VALUE : bg.color;

  const handleModeChange = useCallback(
    (mode: 'custom' | 'inherit') => {
      if (mode === 'inherit') {
        onChange({ ...bg, color: 'none', image: '' });
      } else if (isInherited) {
        onChange({ ...bg, color: 'surface' });
      }
    },
    [bg, isInherited, onChange]
  );

  const handleColorSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const chosen = e.currentTarget.value;
      if (chosen === CUSTOM_OPTION_VALUE) {
        // Only seed the starting hex when not already in custom mode.
        if (!isCustom) onChange({ ...bg, color: CUSTOM_DEFAULT });
      } else {
        onChange({ ...bg, color: chosen });
      }
    },
    [isCustom, bg, onChange]
  );

  const handleHexChange = useCallback(
    // Deliberate fix: previously handleColor persisted raw input verbatim, so pasted
    // arbitrary text would be stored and later emitted as a CSS value — the same hole
    // sanitizeHexInput's doc comment describes for text color. Route through the
    // sanitizer so only valid hex characters reach the store.
    (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...bg, color: sanitizeHexInput(e.currentTarget.value) }),
    [bg, onChange]
  );

  const handleImage = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...bg, image: e.currentTarget.value }),
    [bg, onChange]
  );

  // Library-picked assets store their resolved value under the same `image` key the manual URL
  // input writes to -- see `useAssetPicker`'s doc comment for why the pick-time `resolveImageSrc`
  // conversion happens once, shared with `asset-picker-field.tsx`, rather than being redone here.
  const handlePickImage = useCallback((imageUrl: string) => onChange({ ...bg, image: imageUrl }), [bg, onChange]);
  const { handleOpen: handleOpenPicker, modal: assetPickerModal } = useAssetPicker(
    enterpriseId,
    handlePickImage,
    undefined,
    readOnly
  );

  const handleSize = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => onChange({ ...bg, size: e.currentTarget.value }),
    [bg, onChange]
  );

  const handlePosition = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => onChange({ ...bg, position: e.currentTarget.value }),
    [bg, onChange]
  );

  const handleRepeat = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => onChange({ ...bg, repeat: e.currentTarget.value }),
    [bg, onChange]
  );

  return (
    <FieldLabel label={field.label ?? name} readOnly={readOnly} el='div'>
      <ModeGroup role='radiogroup' aria-label={`${field.label ?? name} mode`}>
        <ModeOption $selected={isInherited}>
          <ModeInput
            type='radio'
            name={`${name}-mode`}
            value='inherit'
            checked={isInherited}
            onChange={() => handleModeChange('inherit')}
            disabled={readOnly}
            data-testid={`bg-mode-${name}-inherit`}
          />
          Inherit
        </ModeOption>
        <ModeOption $selected={!isInherited}>
          <ModeInput
            type='radio'
            name={`${name}-mode`}
            value='custom'
            checked={!isInherited}
            onChange={() => handleModeChange('custom')}
            disabled={readOnly}
            data-testid={`bg-mode-${name}-custom`}
          />
          Custom
        </ModeOption>
      </ModeGroup>

      {!isInherited && (
        <>
          {/* Color role row — semantic theme roles first, custom hex as the escape hatch. */}
          <div style={rowStyle}>
            <span style={labelStyle} aria-hidden='true'>
              Color
            </span>
            <select
              value={colorSelectValue}
              onChange={handleColorSelectChange}
              disabled={readOnly}
              aria-label={`${field.label ?? name} color role`}
              data-testid={`bg-color-role-${name}`}
              style={{ ...selectStyle, flex: 1 }}
            >
              {SURFACE_ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} data-testid={`bg-color-role-${name}-${opt.value}`}>
                  {opt.label}
                </option>
              ))}
              <option value={CUSTOM_OPTION_VALUE} data-testid={`bg-color-role-${name}-custom`}>
                Custom
              </option>
            </select>
          </div>

          {/* Custom hex inputs — only shown when author has chosen a custom color */}
          {isCustom && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <input
                type='color'
                value={COMPLETE_HEX.test(bg.color) ? bg.color : CUSTOM_DEFAULT}
                onChange={handleHexChange}
                disabled={readOnly}
                aria-label={`${field.label ?? name} custom color swatch`}
                data-testid={`bg-color-swatch-${name}`}
                style={{
                  width: 32,
                  height: 32,
                  padding: 0,
                  border: '1px solid #d0d4d8',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              />
              <input
                type='text'
                value={bg.color}
                onChange={handleHexChange}
                disabled={readOnly}
                aria-label={`${field.label ?? name} custom color value`}
                data-testid={`bg-color-text-${name}`}
                style={{ ...inputStyle, flex: 1, fontFamily: 'var(--dcm-cms-bg-field-hex-font-family, monospace)' }}
              />
            </div>
          )}

          <div style={rowStyle}>
            <span style={labelStyle}>Image</span>
            <LibraryUrlTabs
              enterpriseId={enterpriseId}
              fieldLabel={`${field.label ?? name} image`}
              name={`${name}-image`}
              value={bg.image}
              onBrowseLibrary={handleOpenPicker}
              onUrlChange={handleImage}
              readOnly={readOnly}
              browseTestId={`bg-image-browse-${name}`}
              inputTestId={`bg-image-${name}`}
            />
          </div>

          {assetPickerModal}

          {/* Image settings (only when image is set) */}
          {bg.image && (
            <>
              <div style={rowStyle}>
                <span style={labelStyle}>Size</span>
                <select
                  value={bg.size}
                  onChange={handleSize}
                  disabled={readOnly}
                  data-testid={`bg-size-${name}`}
                  style={{ ...selectStyle, flex: 1 }}
                >
                  {SIZE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={rowStyle}>
                <span style={labelStyle}>Position</span>
                <select
                  value={bg.position}
                  onChange={handlePosition}
                  disabled={readOnly}
                  data-testid={`bg-position-${name}`}
                  style={{ ...selectStyle, flex: 1 }}
                >
                  {POSITION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={rowStyle}>
                <span style={labelStyle}>Repeat</span>
                <select
                  value={bg.repeat}
                  onChange={handleRepeat}
                  disabled={readOnly}
                  data-testid={`bg-repeat-${name}`}
                  style={{ ...selectStyle, flex: 1 }}
                >
                  {REPEAT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </>
      )}
    </FieldLabel>
  );
}
